import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Route, Routes } from 'react-router-dom'
import { useLocation, useParams } from 'react-router-dom'
import { buildV3Location } from '../locationUtils'
import GetComponentRoute from './GetComponentRoute'
import GetIndexRouteLoader from './GetIndexRouteLoader'
import LayoutOutlet from './LayoutOutlet'
import OnEnterRedirect from '../redirects/OnEnterRedirect'
import { isModuleIndexPath } from '../viewportWidth'
import { resolveExpandedChildren } from './routeLoaderCache'
import RouteLoadFallback from './RouteLoadFallback'

function promisifyRouteCallback(fn, location) {
  return new Promise((resolve, reject) => {
    try {
      fn(location, (err, result) => {
        if (err) reject(err)
        else resolve(result)
      })
    } catch (error) {
      reject(error)
    }
  })
}

function childPath(path) {
  if (!path) return undefined
  if (path.startsWith('/')) {
    const iframeMatch = path.match(/\/compliance\/iframe\/(.+)$/)
    if (iframeMatch) return `iframe/${iframeMatch[1]}/*`
    return `${path.slice(1)}/*`
  }
  return `${path}/*`
}

function IndexRouteElement({ indexRoute, routeScope }) {
  if (indexRoute?.onEnter) {
    return <OnEnterRedirect onEnter={indexRoute.onEnter} />
  }
  if (indexRoute?.getComponent) {
    return <GetComponentRoute getComponent={indexRoute.getComponent} routeScope={routeScope} />
  }
  if (indexRoute?.component) {
    return <IndexComponentRoute component={indexRoute.component} />
  }
  return null
}

function IndexComponentRoute({ component: IndexComponent }) {
  const location = useLocation()
  const params = useParams()
  return <IndexComponent location={buildV3Location(location, params)} params={params} />
}

async function expandChildRoutes(childDefs, v3Location) {
  const expanded = []

  for (const child of childDefs || []) {
    if (!child.path && child.getChildRoutes) {
      const nested = await promisifyRouteCallback(child.getChildRoutes, v3Location)
      expanded.push({
        kind: 'layout',
        def: child,
        children: nested || [],
      })
    } else {
      expanded.push({ kind: 'route', def: child })
    }
  }

  return expanded
}

function useExpandedChildren(routeDef, store) {
  const location = useLocation()
  const params = useParams()
  const [children, setChildren] = useState(routeDef?.getChildRoutes ? null : [])
  const [error, setError] = useState(null)
  const routeDefRef = useRef(routeDef)
  routeDefRef.current = routeDef
  const loadedExpansionKeyRef = useRef(null)
  // Child route defs are mostly static, but Study getChildRoutes also fetches study data.
  // Include study identity so switching studies (or re-entering after cache clear) re-runs those side effects.
  const expansionKey = params.studyID
    ? `${routeDef?.path ?? 'root'}:${params.studyID}:${params.studyVersionID || ''}`
    : routeDef?.path ?? 'root'

  useEffect(() => {
    const def = routeDefRef.current
    if (!def?.getChildRoutes) return undefined

    if (loadedExpansionKeyRef.current === expansionKey) return undefined

    let cancelled = false
    setError(null)
    setChildren(null)

    const v3Location = buildV3Location(location, params)
    resolveExpandedChildren(expansionKey, () =>
      promisifyRouteCallback(def.getChildRoutes, v3Location).then(childDefs =>
        expandChildRoutes(childDefs, v3Location),
      ),
    )
      .then(expanded => {
        if (!cancelled) {
          loadedExpansionKeyRef.current = expansionKey
          setChildren(expanded)
        }
      })
      .catch(err => {
        if (!cancelled) setError(err)
      })

    return () => {
      cancelled = true
    }
  }, [expansionKey, location, params])

  const loading =
    Boolean(routeDef?.getChildRoutes) && children === null && loadedExpansionKeyRef.current !== expansionKey

  return { children, error, loading }
}

function renderExpandedRoutes(expanded, store, parentScope) {
  return expanded.map((item, index) => {
    if (item.kind === 'layout') {
      const { def, children: nestedChildren } = item
      const layoutScope = def.path ?? parentScope ?? `layout-${index}`
      return (
        <Route key={`layout-${index}`} element={<LayoutOutlet component={def.component} />}>
          {def.getIndexRoute ? (
            <Route
              index
              element={
                <GetIndexRouteLoader
                  key={layoutScope}
                  getIndexRoute={def.getIndexRoute}
                  routeScope={layoutScope}
                />
              }
            />
          ) : null}
          {def.indexRoute ? (
            <Route index element={<IndexRouteElement indexRoute={def.indexRoute} routeScope={layoutScope} />} />
          ) : null}
          {def.getComponent ? (
            <Route
              index
              element={
                <GetComponentRoute
                  key={layoutScope}
                  getComponent={def.getComponent}
                  routeScope={layoutScope}
                />
              }
            />
          ) : null}
          {(nestedChildren || []).map((child, childIndex) => (
            <Route
              key={child.path || `nested-${childIndex}`}
              path={childPath(child.path)}
              element={<V6PlainRoute routeDef={child} store={store} />}
            />
          ))}
        </Route>
      )
    }

    const { def } = item
    const routeScope = def.path ?? parentScope ?? `route-${index}`
    if (!def.path) {
      return (
        <Route
          key={`pathless-${index}`}
          index
          element={
            def.getComponent ? (
              <GetComponentRoute getComponent={def.getComponent} routeScope={routeScope} />
            ) : (
              <V6PlainRoute routeDef={def} store={store} />
            )
          }
        />
      )
    }

    return (
      <Route
        key={def.path}
        path={childPath(def.path)}
        element={<V6PlainRoute routeDef={def} store={store} />}
      />
    )
  })
}

/**
 * Renders a v3 plain-route branch as native React Router v6 nested <Routes>.
 * Parent routes in AppRoutes must use a trailing /* (e.g. path="studies/*").
 */
function V6PlainRoute({ routeDef: routeDefProp, factory, store }) {
  const location = useLocation()
  const routeDef = useMemo(() => {
    if (routeDefProp) return routeDefProp
    if (factory) return factory(store)
    return null
  }, [routeDefProp, factory, store])
  const { children: expanded, error, loading } = useExpandedChildren(routeDef, store)
  const hasIndex = routeDef?.getIndexRoute || routeDef?.indexRoute
  const atModuleIndex = isModuleIndexPath(location.pathname, routeDef?.path)
  const routeScope = routeDef?.path ?? 'root'

  if (error) throw error

  if (routeDef?.getComponent) {
    return (
      <GetComponentRoute
        key={routeScope}
        getComponent={routeDef.getComponent}
        routeScope={routeScope}
      />
    )
  }

  // Nested module paths need child routes loaded; index pages can render immediately.
  if (loading && !(hasIndex && atModuleIndex)) return <RouteLoadFallback />

  const hasChildren = (expanded || []).length > 0
  const childRoutes = atModuleIndex ? [] : expanded || []

  if (!hasIndex && !hasChildren && !routeDef?.component) return null

  if (routeDef?.component) {
    return (
      <Routes>
        <Route element={<LayoutOutlet component={routeDef.component} />}>
          {routeDef.getIndexRoute ? (
            <Route
              index
              element={
                <GetIndexRouteLoader
                  key={routeScope}
                  getIndexRoute={routeDef.getIndexRoute}
                  routeScope={routeScope}
                />
              }
            />
          ) : null}
          {routeDef.indexRoute ? (
            <Route index element={<IndexRouteElement indexRoute={routeDef.indexRoute} routeScope={routeScope} />} />
          ) : null}
          {renderExpandedRoutes(childRoutes, store, routeScope)}
        </Route>
      </Routes>
    )
  }

  return (
    <Routes>
      {routeDef?.getIndexRoute ? (
        <Route
          index
          element={
            <GetIndexRouteLoader
              key={routeScope}
              getIndexRoute={routeDef.getIndexRoute}
              routeScope={routeScope}
            />
          }
        />
      ) : null}
      {routeDef?.indexRoute ? (
        <Route
          index
          element={<IndexRouteElement indexRoute={routeDef.indexRoute} routeScope={routeScope} />}
        />
      ) : null}
      {renderExpandedRoutes(childRoutes, store, routeScope)}
    </Routes>
  )
}

export default V6PlainRoute
